import { History } from "lucide-react";
import * as React from "react";
import { NavLink } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/feedback";
import { Tooltip } from "@/components/ui/misc";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { counterparty, rangeSince } from "@/lib/calls";
import { clockTime, daySummary, legText, railOffset } from "@/lib/phone-page";
import { useCalls, useCallSummary, type CallFilters } from "@/lib/queries";
import { OWN_NON_CALL_LEGS, type Cdr } from "@/lib/types";
import { cn, formatPhoneNumber } from "@/lib/utils";
import { ContactRowLink } from "@/softphone/contact-link";
import { RAIL_CELL, RAIL_LIST_ROW } from "@/softphone/rail";
import { PHONE_COMPANION } from "@/softphone/voicemail-suggestion";

/**
 * "My calls today": the rep's own calls of the day, newest first, one press away on every screen.
 *
 * It was a card in a column of the Phone page, where it took a third of the page's width for a
 * list a rep looks at a few times a day, and where it could not be seen from any other screen.
 * Now it is a standing button in the phone's rail (`MyCallsRailButton`), under the phone's own
 * button and the voicemail icon, and the list opens from it as a popover the way the phone does.
 * A narrow window has no rail, so there the Phone page's strip carries the same list behind a
 * button with the words on it (`MyCallsButton`).
 *
 * Nothing is asked of the server until the list is opened. The two queries live in the body
 * (`MyCallsToday`), which is mounted only while its popover is open, so a rail that is on every
 * page costs no request on any of them, and the minute's refresh runs only while somebody is
 * looking. The Phone page's strip asks for the same summary under the same key, so on that page
 * the header's line is there at once.
 *
 * It lives here and not in `routes/phone.tsx` because the rail is the dock's, and the dock cannot
 * import from a route.
 */

/** The reader's own calls since local midnight, without the legs that are not calls with anybody
 *  (their line into their own room, their sound tests). */
export function todayFilters(day: Date = new Date()): CallFilters {
  return { mine: true, exclude_leg: OWN_NON_CALL_LEGS, since: rangeSince("today", day) };
}

/** Today's own calls, for the counts in the Phone page's strip and for the list here. Pinned per
 *  day, since recomputing midnight on every render would make a new query key every time. */
export function useTodayFilters(): CallFilters {
  const day = new Date().toDateString();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return React.useMemo(() => todayFilters(), [day]);
}

/** Legs of the rep's own history as rows: the other party over what happened, the time at the
 *  right, and room for one control. A leg with a contact is a link to it, over the whole row
 *  (`ContactRowLink`): the list says who a call was with, and the contact is where the rest is. */
export function HistoryList({
  rows, mine, pending, error, empty, dense = false, action, onNavigate,
}: {
  rows: Cdr[];
  mine: readonly string[];
  pending: boolean;
  error: unknown;
  empty: string;
  dense?: boolean;
  action?: (leg: Cdr, party: { number: string | null; name: string | null }) => React.ReactNode;
  /** The rep followed a row to its contact: the popover around the list closes itself. */
  onNavigate?: () => void;
}) {
  if (pending) {
    return <div className="flex justify-center py-6"><Spinner /></div>;
  }
  if (error) {
    return (
      <p className="px-4 py-3 text-xs text-text-muted">
        {error instanceof ApiError ? error.message : "Could not load your calls."}
      </p>
    );
  }
  if (rows.length === 0) {
    return <p className={cn("text-xs text-text-muted", dense ? "px-4 py-3" : "py-2")}>{empty}</p>;
  }
  return (
    <ul className={cn("divide-y divide-border", !dense && "rounded-md border border-border")}>
      {rows.map((leg) => {
        const party = counterparty(leg, mine);
        const who = party.name ?? (party.number ? formatPhoneNumber(party.number) : "Unknown");
        return (
          <li
            key={leg.id}
            className={cn("flex items-center gap-2.5", dense ? "px-4 py-2" : "px-3 py-2", RAIL_LIST_ROW)}
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm text-text">
                <ContactRowLink leadId={leg.lead_id} name={who} onNavigate={onNavigate} />
              </span>
              <span className="block truncate text-xs text-text-muted">{legText(leg, mine)}</span>
            </span>
            <span className="shrink-0 text-xs tabular text-text-subtle" title={leg.start_time ?? undefined}>
              {clockTime(leg.start_time)}
            </span>
            {/* Over the row's link to the contact, which is stretched across the row. */}
            {action && <span className="relative flex shrink-0">{action(leg, party)}</span>}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * What the popover holds: the day in a line, the newest calls, and the way to the full history.
 *
 * The header and the link stay where they are and the list scrolls between them, so a long day
 * on a short window still shows how to get to the rest. The day's line is simply absent until the
 * totals arrive, rather than saying "0 calls" for a second to a rep who has made forty.
 *
 * With nobody signed in (the gallery at `/dev/phone`) `mine` is an empty list and the two requests
 * fail, which reads as the list's own error text.
 */
export function MyCallsToday({
  mine, rows = 10, onNavigate,
}: {
  /** Every identifier the account's phone answers to, which is how a leg tells "you" from "them". */
  mine: readonly string[];
  /** How many of the newest calls are listed before the link takes over. */
  rows?: number;
  /** The rep followed a row to its contact, or the link to Call history: the popover around this
   *  closes itself. */
  onNavigate?: () => void;
}) {
  const filters = useTodayFilters();
  const summary = useCallSummary(filters, { refetchInterval: 60_000 });
  const list = useCalls(filters, 0, { refetchInterval: 60_000 });
  const { calls, minutes } = daySummary(summary.data);
  const total = list.data?.total ?? 0;
  const shown = (list.data?.items ?? []).slice(0, rows);
  return (
    <div className="flex min-h-0 flex-col">
      <div className="shrink-0 border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold text-text">My calls today</h2>
        {summary.data && (
          <p className="text-xs tabular text-text-muted">
            {calls} {calls === 1 ? "call" : "calls"} · {minutes} min on the phone
          </p>
        )}
      </div>
      <div className="min-h-0 overflow-y-auto scrollbar-subtle">
        <HistoryList
          rows={shown}
          mine={mine}
          pending={list.isPending}
          error={list.error}
          empty="No calls yet today."
          dense
          onNavigate={onNavigate}
        />
      </div>
      <div className="shrink-0 border-t border-border px-4 py-2 text-xs">
        {/* A plain class name, and not under a Radix Slot: a Slot joins class names as text. */}
        <NavLink
          to="/history"
          className="inline-flex items-center gap-1.5 font-medium text-accent-11 hover:underline"
          onClick={onNavigate}
        >
          <History className="size-3.5" aria-hidden />
          {total > shown.length ? `${total - shown.length} more in Call history` : "Call history"}
        </NavLink>
      </div>
    </div>
  );
}

/** The popover's box, the same from the rail and from the page's strip. */
const CONTENT = "flex w-80 flex-col p-0 max-h-[var(--radix-popover-content-available-height)]";

/**
 * The list in the phone's rail: a standing button under the phone's own, on every page.
 *
 * It is a popover of its own, with its own root. The rail is drawn inside the phone's popover
 * root, so a trigger without a root of its own would open and close the phone. The cell and the
 * popover carry the companion mark, so the phone does not read a press on either as a click
 * somewhere else on the page and close itself: a rep on a call may look at the day's list with
 * the call still open beside it. That is also why it opens further out while the phone's popover
 * or the voicemail suggestion is open (`railOffset`): beside them, never over the call.
 *
 * It only ever opens by the rep's press. A click elsewhere or Escape closes it, as does following
 * a row to its contact, and it steps out of the way by itself when a call starts ringing or a
 * voicemail is suggested, since both open in the same corner and both want an answer.
 *
 * The tooltip is for a pointer only, as the phone button's is: closing the popover hands focus
 * back to this button, and a tooltip that opened on focus would pop up every time. It is also held
 * shut while the popover is open, where it would say what the popover's header already says.
 */
export function MyCallsRailButton({
  phoneOpen, suggestionOpen, ringing,
}: {
  /** The phone's own popover is open. */
  phoneOpen: boolean;
  /** A voicemail is suggested and its choices are open (`VoicemailRailButton`). */
  suggestionOpen: boolean;
  /** A call is ringing this phone. */
  ringing: boolean;
}) {
  const mine = useAuth().user?.sip_endpoint_numbers ?? [];
  const [open, setOpen] = React.useState(false);
  const [tipOpen, setTipOpen] = React.useState(false);
  React.useEffect(() => {
    if (ringing || suggestionOpen) setOpen(false);
  }, [ringing, suggestionOpen]);

  const companion = { [PHONE_COMPANION]: "" };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div
          className={RAIL_CELL}
          {...companion}
        >
          <Tooltip
            content="My calls today"
            side="left"
            open={tipOpen && !open}
            onOpenChange={setTipOpen}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                // Radix opens the tooltip from a focus handler that stands down for a prevented event.
                onFocus={(e) => e.preventDefault()}
                aria-label="My calls today"
                className={cn(
                  "flex size-9 items-center justify-center rounded-full transition-colors",
                  open
                    ? "bg-frame-hover text-text"
                    : "text-text-muted hover:bg-frame-hover hover:text-text",
                )}
              >
                <History className="size-[18px]" aria-hidden />
              </button>
            </PopoverTrigger>
          </Tooltip>
        </div>
      </PopoverAnchor>
      <PopoverContent
        side="left"
        align="start"
        sideOffset={railOffset({ phoneOpen, suggestionOpen })}
        aria-label="My calls today"
        className={CONTENT}
        {...companion}
      >
        <MyCallsToday mine={mine} onNavigate={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}

/**
 * The same list for a narrow window, where the phone has no rail: a button with its words on it,
 * for the Phone page's strip, and the list under it.
 */
export function MyCallsButton({ mine }: { mine: readonly string[] }) {
  const [open, setOpen] = React.useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline">
          <History />
          My calls today
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        aria-label="My calls today"
        className={CONTENT}
        {...{ [PHONE_COMPANION]: "" }}
      >
        <MyCallsToday mine={mine} onNavigate={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}
